import { Stack, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material"
import { StandardSelector } from "lib/@components/combos/standard-selector"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { setFromValueParamOnlyTruthy } from "lib/setFromEvent"

const COMMENT_FILTER_FONT_SIZE = "12px"

// Triage controls for the regime-level Comments directory: a segmented status filter plus recipient
// and schedule (target) dropdowns. The toolbar owns its own params (regimeCommentFilter /
// regimeCommentRecipient / regimeCommentTarget) and the panel reads the same params to filter rows.
// It also adopts an incoming `commentTarget` schedule id — set when a schedule-code indicator opens
// this tab — as the active target until the user picks a different schedule.
export function ShareLinkCommentRegimeToolbar({ comments }) {
    const theme = useTheme()
    const [status, setStatus] = useSearchParam("regimeCommentFilter", "open")
    const [recipient, setRecipient] = useSearchParam("regimeCommentRecipient", "all")
    const [target, setTarget] = useSearchParam("regimeCommentTarget", "all")
    const [incomingTarget] = useSearchParam("commentTarget", null)
    const targetOptions = getRegimeTargetOptions(comments)
    const recipientOptions = getRegimeRecipientOptions(comments)
    const effectiveTarget = resolveEffectiveTarget()

    return (
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            data-cy="share-link-regime-comments-toolbar"
            sx={{ flexWrap: "wrap", py: 0.5 }}
        >
            <ToggleButtonGroup
                exclusive
                size="small"
                value={status}
                onChange={setFromValueParamOnlyTruthy(setStatus)}
                data-cy="share-link-regime-comments-filter"
                sx={getToggleSx()}
            >
                <ToggleButton value="open">Open</ToggleButton>
                <ToggleButton value="resolved">Resolved</ToggleButton>
                <ToggleButton value="all">All</ToggleButton>
            </ToggleButtonGroup>
            <StandardSelector
                value={recipient}
                onChange={setRecipient}
                options={recipientOptions}
                data-cy="share-link-regime-comments-recipient"
                size="small"
                sx={getSelectorSx()}
                optionSx={getSelectorOptionSx()}
            />
            <StandardSelector
                value={effectiveTarget}
                onChange={setTarget}
                options={targetOptions}
                data-cy="share-link-regime-comments-target"
                size="small"
                sx={getSelectorSx()}
                optionSx={getSelectorOptionSx()}
            />
        </Stack>
    )

    // Compact, dense triage controls — caption-sized text and tight padding so the filter bar stays
    // small relative to the comment list.
    function getToggleSx() {
        return {
            "& .MuiToggleButton-root": {
                px: 1,
                py: 0.25,
                fontSize: COMMENT_FILTER_FONT_SIZE,
                lineHeight: 1.6,
                textTransform: "none",
            },
        }
    }

    function getSelectorSx() {
        return {
            px: 1,
            py: 0.25,
            minHeight: 28,
            fontSize: COMMENT_FILTER_FONT_SIZE,
            lineHeight: 1.6,
            textTransform: "none",
            fontWeight: theme.typography.fontWeightMedium,
        }
    }

    function getSelectorOptionSx() {
        return {
            py: 0.75,
            fontSize: COMMENT_FILTER_FONT_SIZE,
        }
    }

    // The user's explicit schedule pick wins; otherwise adopt an incoming commentTarget that matches
    // a known schedule; otherwise show all schedules.
    function resolveEffectiveTarget() {
        if (target && target !== "all") return target
        if (incomingTarget && targetOptions.some((option) => option.value === incomingTarget)) return incomingTarget
        return "all"
    }
}

// Exported for direct unit testing. Build the schedule (target) dropdown options from the distinct
// schedule ids present in the visible comments, preserving first-seen order.
export function getRegimeTargetOptions(comments) {
    const options = [{ value: "all", label: "All schedules" }]
    const seen = new Set(["all"])
    for (const comment of comments ?? []) {
        const value = comment?.scheduleId
        if (!value || seen.has(value)) continue
        seen.add(value)
        options.push({ value, label: comment.scheduleTitle || "Schedule" })
    }
    return options
}

// Exported for direct unit testing. Build the recipient/author dropdown options from the distinct
// names present in the visible comments, preserving first-seen order.
export function getRegimeRecipientOptions(comments) {
    const options = [{ value: "all", label: "All people" }]
    const seen = new Set(["all"])
    for (const comment of comments ?? []) {
        const value = comment?.recipientName ?? comment?.authorName
        if (!value || seen.has(value)) continue
        seen.add(value)
        options.push({ value, label: value })
    }
    return options
}
